import React, { useEffect, useState } from 'react'
import MaskedInput from 'react-text-mask'
import { ModalWrapper } from '../../../components/modalWrapper'
import {
  StartAdornmentField,
  START_ADORNMENT_INPUT_CLASS,
} from '../../../components/inputs/startAdornment'
import { PHONE_MASK, PHONE_PLACEHOLDER } from '../../../utils/phoneMask'
import { EMAIL_PLACEHOLDER, EMAIL_REGEX } from '../../../utils/emailValidation'
import './ContactInfoModal.css'

interface ContactInfo {
  phone: string
  email: string
}

interface PhoneModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (contactInfo: ContactInfo) => void
  phone?: string | null
  email?: string | null
  showPhone?: boolean
  showEmail?: boolean
  title?: string
  description?: string
  confirmButtonText?: string
  isLoading?: boolean
  serverError?: string | null
}

const PhoneModal: React.FC<PhoneModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  phone = null,
  email = null,
  showPhone = true,
  showEmail = true,
  title,
  description,
  confirmButtonText = 'Confirm',
  isLoading = false,
  serverError = null,
}) => {
  // Dynamic defaults based on what's being shown
  const defaultTitle = showPhone && showEmail 
    ? 'Update Contact Information'
    : showPhone 
    ? 'Update Mobile Number'
    : 'Update Email Address'
  
  const defaultDescription = showPhone && showEmail
    ? 'Please enter the customer\'s phone number and email address.'
    : showPhone
    ? 'Please enter the customer\'s mobile number.'
    : 'Please enter the customer\'s email address.'
  const [phoneValue, setPhoneValue] = useState('')
  const [emailValue, setEmailValue] = useState('')
  const [errors, setErrors] = useState<{ phone?: string; email?: string }>({})
  const [showServerError, setShowServerError] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setPhoneValue(phone || '')
      setEmailValue(email || '')
      setErrors({})
      setShowServerError(true)
    }
  }, [isOpen, phone, email])

  useEffect(() => {
    if (serverError) {
      setShowServerError(true)
    }
  }, [serverError])

  const sanitizePhone = (input: string) => input.replace(/\D/g, '')

  const validatePhone = (input: string) => {
    if (!input || sanitizePhone(input).length < 10) {
      return 'Phone number (enter a valid number)'
    }
    return null
  }

  const validateEmail = (input: string) => {
    if (!input || input.trim() === '') {
      return 'Email address is required'
    }
    if (!EMAIL_REGEX.test(input.trim())) {
      return 'Email format is invalid'
    }
    return null
  }

  const handleConfirm = () => {
    const phoneError = showPhone ? validatePhone(phoneValue) : null
    const emailError = showEmail ? validateEmail(emailValue) : null
    
    if (phoneError || emailError) {
      setErrors({
        phone: phoneError || undefined,
        email: emailError || undefined,
      })
      return
    }
    
    const contactInfo: ContactInfo = {
      phone: showPhone ? sanitizePhone(phoneValue) : (phone || ''),
      email: showEmail ? emailValue.trim() : (email || ''),
    }
    
    onConfirm(contactInfo)
  }

  const handleClose = () => {
    setPhoneValue('')
    setEmailValue('')
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <ModalWrapper
      title={title || defaultTitle}
      hideModal={handleClose}
      xclose={true}
      width="500px"
      height="auto"
    >
      <div className="contact-info-modal-content">
        <p className="contact-info-modal-description">
          {description || defaultDescription}
        </p>

        {showPhone && (
          <>
            <label
              htmlFor="phone-input"
              className="contact-info-modal-label"
            >
              Cell Phone
            </label>
            <StartAdornmentField adornment="+1">
              <MaskedInput
                id="phone-input"
                type="tel"
                value={phoneValue}
                onChange={(e) => {
                  setPhoneValue(e.target.value)
                  if (errors.phone) {
                    setErrors({ ...errors, phone: undefined })
                  }
                }}
                placeholder={PHONE_PLACEHOLDER}
                mask={PHONE_MASK}
                className={`contact-info-modal-input ${START_ADORNMENT_INPUT_CLASS} ${errors.phone ? 'contact-info-modal-input--error' : ''}`}
              />
            </StartAdornmentField>
            {errors.phone && (
              <div className="contact-info-modal-error">{errors.phone}</div>
            )}
          </>
        )}

        {showEmail && (
          <>
            <label
              htmlFor="email-input"
              className={`contact-info-modal-label ${showPhone ? 'contact-info-modal-label--with-margin' : ''}`}
            >
              Email Address
            </label>
            <input
              id="email-input"
              type="email"
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value)
                if (errors.email) {
                  setErrors({ ...errors, email: undefined })
                }
              }}
              onFocus={() => setShowServerError(false)}
              placeholder={EMAIL_PLACEHOLDER}
              className={`contact-info-modal-input ${errors.email ? 'contact-info-modal-input--error' : ''}`}
            />
            {errors.email && (
          <div className="contact-info-modal-error">
            {errors.email}
          </div>
        )}
          </>
        )}

        {serverError && showServerError && (
          <div className="contact-info-modal-server-error">
            {serverError}
          </div>
        )}

        <div className="modal__buttons">
          <button
            className="button button-drive-america"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : confirmButtonText}
          </button>
          <button
            className="button button-drive-america"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  )
}

export default PhoneModal
